import api, { invalidateMonitoringSettingsCache } from './api';

export const PRIMARY_POOLS_UPDATED_EVENT = 'primary-pools-updated';

export type PrimaryPoolSlot = {
  code: string;
  order: number;
};

export type DashboardSettingsPayload = {
  primary_pool_codes: string[];
  primary_pools: PrimaryPoolSlot[];
};

function parseDashboardPayload(data: unknown): DashboardSettingsPayload {
  const dash = (data as { dashboard?: Record<string, unknown> })?.dashboard ?? {};
  const rawPools = dash.primary_pools;
  const slots: PrimaryPoolSlot[] = [];
  if (Array.isArray(rawPools)) {
    for (const item of rawPools) {
      if (!item || typeof item !== 'object') continue;
      const code = String((item as { code?: string }).code || '').trim();
      const order = Number((item as { order?: number }).order);
      if (code && Number.isFinite(order) && order >= 1) {
        slots.push({ code, order: Math.floor(order) });
      }
    }
  }
  slots.sort((a, b) => a.order - b.order || a.code.localeCompare(b.code, 'ko'));

  let codes: string[] = [];
  const rawCodes = dash.primary_pool_codes;
  if (Array.isArray(rawCodes)) {
    codes = rawCodes.map((c) => String(c).trim()).filter(Boolean);
  }
  if (slots.length > 0) {
    codes = slots.map((s) => s.code);
  }
  return { primary_pool_codes: codes, primary_pools: slots };
}

/** @deprecated 순번 없이 코드만 — fetchPrimaryPoolSlots 권장 */
export async function fetchPrimaryPoolCodes(): Promise<string[]> {
  const { primary_pool_codes } = await fetchPrimaryPoolSlotsPayload();
  return primary_pool_codes;
}

export async function fetchPrimaryPoolSlots(): Promise<PrimaryPoolSlot[]> {
  const { primary_pools } = await fetchPrimaryPoolSlotsPayload();
  return primary_pools;
}

async function fetchPrimaryPoolSlotsPayload(): Promise<DashboardSettingsPayload> {
  const res = await api.get<Record<string, unknown>>('/api/settings/system', undefined, {
    useCache: false,
  });
  return parseDashboardPayload(res);
}

export async function savePrimaryPoolSlots(slots: PrimaryPoolSlot[]): Promise<void> {
  const normalized = slots
    .map((s) => ({
      code: String(s.code).trim(),
      order: Math.floor(Number(s.order)),
    }))
    .filter((s) => s.code && s.order >= 1)
    .sort((a, b) => a.order - b.order || a.code.localeCompare(b.code, 'ko'));

  const primary_pool_codes = normalized.map((s) => s.code);

  await api.post('/api/settings/system', {
    dashboard: {
      primary_pools: normalized,
      primary_pool_codes,
    },
  });
  invalidateMonitoringSettingsCache();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PRIMARY_POOLS_UPDATED_EVENT));
  }
}

/** @deprecated savePrimaryPoolSlots 사용 */
export async function savePrimaryPoolCodes(codes: string[]): Promise<void> {
  const slots = codes.map((code, i) => ({ code: String(code).trim(), order: i + 1 }));
  await savePrimaryPoolSlots(slots.filter((s) => s.code));
}
